"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Metadata = exports.MetadataState = void 0;
var tslib_1 = require("tslib");
var Acl_1 = require("../Acl");
var util_1 = require("../util");
var error_1 = require("../error");
var MetadataState;
(function (MetadataState) {
    MetadataState[MetadataState["UNAVAILABLE"] = -1] = "UNAVAILABLE";
    MetadataState[MetadataState["PERSISTENT"] = 0] = "PERSISTENT";
    MetadataState[MetadataState["DIRTY"] = 1] = "DIRTY";
})(MetadataState = exports.MetadataState || (exports.MetadataState = {}));
/**
 * The Metadata instance tracks the state of an object and checks if the object state was changed since last
 * load/update. The metadata keeps therefore the state of:
 * - in which state the object currently is
 * - which db managed the instance
 * - the metadata of the object (id, version, bucket)
 * - which is the owning object (root object) of an embedded object
 *
 * {@link Metadata#get(object)} can be used on any managed object to retrieve the metadata of the root object
 */
var Metadata = /** @class */ (function (_super) {
    tslib_1.__extends(Metadata, _super);
    /**
     * @param type
     */
    function Metadata(type) {
        var _this = _super.call(this) || this;
        _this.entityManager = null;
        _this.decodedKey = null;
        _this.id = null;
        _this.state = MetadataState.DIRTY;
        _this.enabled = true;
        _this.id = null;
        _this.version = null;
        _this.type = type;
        _this.acl = new Acl_1.Acl();
        return _this;
    }
    Metadata.create = function (type, db) {
        if (type.isEntity) {
            return new Metadata(type);
        }
        if (type.isEmbeddable) {
            return { type: type, db: db, setDirty: function () { } };
        }
        throw new Error("Illegal type ".concat(type));
    };
    /**
     * Returns the metadata of the managed object
     * @param managed
     * @return
     */
    Metadata.get = function (managed) {
        // eslint-disable-next-line no-underscore-dangle
        return managed._metadata;
    };
    Object.defineProperty(Metadata.prototype, "db", {
        /**
         * @type EntityManager
         */
        get: function () {
            if (this.entityManager) {
                return this.entityManager;
            }
            this.entityManager = require('../baqend').db; // eslint-disable-line global-require
            return this.entityManager;
        },
        /**
         * @param db
         */
        set: function (db) {
            if (!this.entityManager) {
                this.entityManager = db;
            }
            else {
                throw new Error('DB has already been set.');
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Metadata.prototype, "bucket", {
        /**
         * @type string
         * @readonly
         */
        get: function () {
            return this.type.name;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Metadata.prototype, "key", {
        /**
         * @type string
         * @readonly
         */
        get: function () {
            if (!this.decodedKey && this.id) {
                var index = this.id.lastIndexOf('/');
                this.decodedKey = decodeURIComponent(this.id.substring(index + 1));
            }
            return this.decodedKey;
        },
        /**
         * @param value
         */
        set: function (value) {
            var val = "".concat(value);
            if (this.id) {
                throw new Error('The id can\'t be set twice.');
            }
            this.id = "/db/".concat(this.bucket, "/").concat(encodeURIComponent(val));
            this.decodedKey = val;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Metadata.prototype, "isAttached", {
        /**
         * Indicates if this object already belongs to an db
         * <code>true</code> if this object belongs already to an db otherwise <code>false</code>
         * @type boolean
         * @readonly
         */
        get: function () {
            return !!this.entityManager;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Metadata.prototype, "isAvailable", {
        /**
         * Indicates if this object is represents a db object, but was not loaded up to now
         * @type boolean
         * @readonly
         */
        get: function () {
            return this.state > MetadataState.UNAVAILABLE;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Metadata.prototype, "isPersistent", {
        /**
         * Indicates if this object represents the state of the db and was not modified in any manner
         * @type boolean
         * @readonly
         */
        get: function () {
            return this.state === MetadataState.PERSISTENT;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Metadata.prototype, "isDirty", {
        /**
         * Indicates that this object was modified and the object was not written back to the db
         * @type boolean
         * @readonly
         */
        get: function () {
            return this.state === MetadataState.DIRTY;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Enable/Disable state change tracking of this object
     * @param newStateTrackingState The new change tracking state
     * @return
     */
    Metadata.prototype.enable = function (newStateTrackingState) {
        this.enabled = newStateTrackingState;
    };
    /**
     * Throws the corresponding error if a property is accessed before the owning object is loaded
     * @throws an exception if the object properties aren't available and the object is enabled
     */
    Metadata.prototype.throwUnloadedPropertyAccess = function (property) {
        if (this.enabled && !this.isAvailable) {
            throw new error_1.PersistentError("Illegal property access on ".concat(this.id, "#").concat(property, " , ensure that this reference is loaded before it's properties are accessed."));
        }
    };
    /**
     * Indicates that the associated object isn't available
     * @return
     */
    Metadata.prototype.setUnavailable = function () {
        this.state = MetadataState.UNAVAILABLE;
    };
    /**
     * Indicates that the associated object is not stale
     *
     * An object is stale if it correlates the database state and is not modified by the user.
     *
     * @return
     */
    Metadata.prototype.setPersistent = function () {
        this.state = MetadataState.PERSISTENT;
    };
    /**
     * Indicates the the object is modified by the user
     * @return
     */
    Metadata.prototype.setDirty = function () {
        this.state = MetadataState.DIRTY;
    };
    /**
     * Indicates the the object is removed
     * @return
     */
    Metadata.prototype.setRemoved = function () {
        // mark the object only as dirty if it was already available
        if (this.isAvailable) {
            this.setDirty();
            this.version = null;
        }
    };
    return Metadata;
}(util_1.Lockable));
exports.Metadata = Metadata;
//# sourceMappingURL=data:application/json;base64,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